import {
  PREPUBLISH_RENDER_PIPELINE_VERSION,
  PUBLISH_PRERENDER_WATERMARK_IDENTITY,
  buildPublishPrerenderKey,
} from "@scroll/shared-utils";

export type PublishPrerenderStatus =
  | "uploading"
  | "queued"
  | "processing"
  | "ready"
  | "failed"
  | "superseded"
  | "expired"
  | "promoted";

export interface PublishPrerenderRecord {
  id: number;
  renderKey: string;
  status: PublishPrerenderStatus;
  outputMuxPlaybackId?: string | null;
  errorMessage?: string | null;
}

export function createPublishSessionId(): string {
  return `publish-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function buildClientPublishPrerenderKey(input: {
  timelineProject: unknown;
  linkOverlays: unknown;
  voiceover: unknown;
  applyWatermark: boolean;
}): string {
  return buildPublishPrerenderKey({
    pipelineVersion: PREPUBLISH_RENDER_PIPELINE_VERSION,
    watermarkIdentity: PUBLISH_PRERENDER_WATERMARK_IDENTITY,
    ...input,
  });
}

export function formatPublishPrerenderErrorMessage(
  message: string | null | undefined,
): string {
  const fallback = "The final video render failed. Please try publishing again.";
  const raw = message?.trim();
  if (!raw) return fallback;

  const ffmpegMatch = raw.match(/ffmpeg exited \d+:\s*([\s\S]*)$/i);
  if (ffmpegMatch) {
    const detail = ffmpegMatch[1]
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean)
      .reverse()
      .find((line) =>
        /error|failed|invalid|not found|unable|cannot|unknown/i.test(line)
        && !/^configuration:/i.test(line)
      );
    return detail
      ? `Video rendering failed during final processing: ${detail}`
      : "Video rendering failed during final processing. Please try publishing again.";
  }

  const singleLine = raw.replace(/\s+/g, " ");
  return singleLine.length > 220 ? `${singleLine.slice(0, 217)}...` : singleLine;
}

export function shouldCompletePublishPrerenderSource(
  status: PublishPrerenderStatus | string | null | undefined,
): boolean {
  return status === "uploading";
}

export function shouldRetryPublishPrerenderSourceComplete({
  sourceReady,
  status,
}: {
  sourceReady: boolean;
  status: PublishPrerenderStatus | string | null | undefined;
}): boolean {
  return !sourceReady && status === "uploading";
}

export function getPublishPrerenderPostAction(
  status: PublishPrerenderStatus | string | null | undefined,
): "publish-rendered" | "wait" | "error" {
  if (status === "ready") return "publish-rendered";
  if (status === "uploading" || status === "queued" || status === "processing") return "wait";
  return "error";
}

function createAbortError(): Error {
  const err = new Error("The request was cancelled.");
  err.name = "AbortError";
  return err;
}

function waitForDelay(ms: number, signal?: AbortSignal): Promise<void> {
  if (signal?.aborted) return Promise.reject(createAbortError());
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(resolve, ms);
    signal?.addEventListener("abort", () => {
      window.clearTimeout(timer);
      reject(createAbortError());
    }, { once: true });
  });
}

function isPrerenderRecord(value: unknown): value is PublishPrerenderRecord {
  return !!value
    && typeof value === "object"
    && typeof (value as { id?: unknown }).id === "number"
    && typeof (value as { status?: unknown }).status === "string";
}

function assertPrerenderResponse<T extends { prerender: PublishPrerenderRecord }>(
  body: unknown,
  stage: string,
): T {
  const prerender = (body as { prerender?: unknown } | null | undefined)?.prerender;
  if (!isPrerenderRecord(prerender)) {
    console.warn("[publish-prerender] malformed server response", { stage, body });
    throw new Error("The render service returned an unexpected response. Please try again.");
  }
  return body as T;
}

export async function fetchPublishPrerenderConfig(): Promise<{
  enabled: boolean;
  pipelineVersion: number;
}> {
  const res = await fetch("/api/publish-prerenders/config", {
    credentials: "include",
  });
  if (!res.ok) {
    return { enabled: false, pipelineVersion: PREPUBLISH_RENDER_PIPELINE_VERSION };
  }
  return res.json();
}

export async function startPublishPrerender(args: {
  publishSessionId: string;
  renderKey: string;
  timelineProject: unknown;
  linkOverlays: unknown;
  voiceover: unknown;
  applyWatermark: boolean;
}): Promise<{ prerender: PublishPrerenderRecord; uploadUrl: string | null }> {
  const res = await fetch("/api/publish-prerenders", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      publishSessionId: args.publishSessionId,
      renderKey: args.renderKey,
      pipelineVersion: PREPUBLISH_RENDER_PIPELINE_VERSION,
      timelineProject: args.timelineProject,
      linkOverlays: args.linkOverlays,
      voiceover: args.voiceover,
      applyWatermark: args.applyWatermark,
    }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null) as { error?: string } | null;
    const err = new Error(`pre-render start failed (${res.status})`) as Error & {
      status?: number;
      serverError?: string;
    };
    err.status = res.status;
    if (body?.error) err.serverError = body.error;
    throw err;
  }
  const body = await res.json().catch(() => null);
  return assertPrerenderResponse<{ prerender: PublishPrerenderRecord; uploadUrl: string | null }>(body, "start");
}

export async function fetchPublishPrerender(
  id: number,
): Promise<{ prerender: PublishPrerenderRecord }> {
  const res = await fetch(`/api/publish-prerenders/${id}`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`pre-render fetch failed (${res.status})`);
  const body = await res.json().catch(() => null);
  return assertPrerenderResponse<{ prerender: PublishPrerenderRecord }>(body, "poll");
}

export async function completePublishPrerenderSource(
  id: number,
  options: { signal?: AbortSignal } = {},
): Promise<{ prerender: PublishPrerenderRecord; sourceReady: boolean }> {
  const res = await fetch(`/api/publish-prerenders/${id}/source-complete`, {
    method: "POST",
    credentials: "include",
    signal: options.signal,
  });
  if (!res.ok && res.status !== 202) throw new Error(`pre-render source-complete failed (${res.status})`);
  const body = await res.json().catch(() => null);
  return assertPrerenderResponse<{ prerender: PublishPrerenderRecord; sourceReady: boolean }>(body, "source-complete");
}

export async function waitForPublishPrerenderSourceComplete(
  id: number,
  options: {
    timeoutMs?: number;
    intervalMs?: number;
    signal?: AbortSignal;
  } = {},
): Promise<{ prerender: PublishPrerenderRecord; sourceReady: boolean }> {
  const timeoutMs = options.timeoutMs ?? 60_000;
  const intervalMs = options.intervalMs ?? 2_000;
  const startedAt = Date.now();
  let latest = await completePublishPrerenderSource(id, { signal: options.signal });
  while (shouldRetryPublishPrerenderSourceComplete({
    sourceReady: latest.sourceReady,
    status: latest.prerender.status,
  })) {
    if (options.signal?.aborted) throw createAbortError();
    if (Date.now() - startedAt >= timeoutMs) return latest;
    await waitForDelay(intervalMs, options.signal);
    latest = await completePublishPrerenderSource(id, { signal: options.signal });
  }
  return latest;
}

export async function cancelPublishPrerender(id: number): Promise<void> {
  await fetch(`/api/publish-prerenders/${id}/cancel`, {
    method: "POST",
    credentials: "include",
  }).catch(() => {});
}

export async function fallbackPublishPrerender(id: number): Promise<void> {
  const res = await fetch(`/api/publish-prerenders/${id}/fallback`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) throw new Error(`pre-render fallback failed (${res.status})`);
}

export function uploadPrerenderSource(args: {
  uploadUrl: string;
  file: File;
  signal?: AbortSignal;
  onProgress?: (progress: number) => void;
}): Promise<void> {
  if (args.signal?.aborted) return Promise.reject(createAbortError());
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const abort = () => xhr.abort();

    args.signal?.addEventListener("abort", abort, { once: true });
    xhr.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable) {
        args.onProgress?.(Math.round((event.loaded / event.total) * 100));
      }
    });
    xhr.addEventListener("load", () => {
      args.signal?.removeEventListener("abort", abort);
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
        return;
      }
      reject(new Error(`pre-render source upload failed (${xhr.status})`));
    });
    xhr.addEventListener("error", () => {
      args.signal?.removeEventListener("abort", abort);
      reject(new Error("pre-render source upload failed"));
    });
    xhr.addEventListener("abort", () => {
      args.signal?.removeEventListener("abort", abort);
      reject(createAbortError());
    });
    xhr.open("PUT", args.uploadUrl);
    xhr.setRequestHeader("content-type", args.file.type || "video/mp4");
    xhr.send(args.file);
  });
}
